/**
 * SharingDetailPanel — the Sharing registry's one detail component: a single
 * pane with two speeds, riding `RightPanelManager` via `right-panel:open`.
 *
 * - **Queue run** (`_mode === 'queue'`, "Review all" / the chat ceremony /
 *   `?queue=`): opens on the **overview** — one row per topic in the group,
 *   each with its seeded level on a Private / Basic / Full switch, over one
 *   black **Approve & share**. A user who trusts the suggestions is done in
 *   one tap. **Review one by one** walks each topic's detail (Back / Next,
 *   Finish); every step stages, and Finish lands back on the overview, which
 *   then reads as the final review. Tapping an overview row drills into that
 *   topic's detail alone (Done returns). The run ends in ONE atomic submit of
 *   the whole group (`host.submitGroup`), which also stamps this member's
 *   group confirm — so Approve stays available while the group is
 *   unconfirmed even when every switch equals its committed state.
 * - **Single topic** (`_mode === 'single'`, a registry row): the same detail
 *   layout for one unit, with its own **Approve & share** (staged, then
 *   committed through `approveUnit` / `setUnitState`) or **Done** when
 *   nothing changed.
 *
 * The click grammar is the same everywhere: a radio or switch STAGES, a black
 * button COMMITS. Because the click itself no longer writes anything, every
 * unit's staged value is pre-filled on open (`seedStaged`: committed state,
 * else the suggested depth, else Private) — the button is the explicit
 * accept. Closing the panel discards staging (no draft layer, no prompt).
 *
 * `contentType`s: `sharing-overview` (the run's overview phase),
 * `sharing-queue` (a walk step or drill), `sharing-unit` (single).
 *
 * Four rules this file exists to enforce:
 *
 * 1. **A root emit always builds a FRESH detached root.** Re-emitting
 *    `right-panel:open` with the *same* node makes `RightPanelManager._setContent`
 *    fire the previous payload's `onDispose` and then re-append the node it just
 *    tore down. Step changes *within* the unit phase mutate that root in place
 *    (the questionnaire-pager pattern); overview ⇄ unit and any mode change
 *    re-root and emit again.
 * 2. **`onDispose` belongs to the emit that registered it.** Each emit
 *    captures a monotonically increasing token; a disposer whose token is stale
 *    no-ops, so an outgoing root can never tear down the one that replaced it.
 *    The same token gates every write's late continuation (batch submit AND
 *    single approve): a panel closed and reopened mid-flight neither repaints,
 *    re-enables, nor closes on the old promise — while the ledger rollback, the
 *    registry repaint and the error toast always land, because server truth
 *    and a refused-write message do not belong to any one run.
 * 3. **A write is awaited before anything moves.** A host seam may return a
 *    promise (the real API path); the panel then disables its controls, lets
 *    the host replace the row(s) from the server's canonical payload, restores
 *    the prior value if the call rejects, and only afterwards repaints, closes,
 *    or toasts. A seam that returns no thenable (the fixture harness) runs
 *    synchronously, with nothing to disable.
 * 4. **Display reads `_pending`, seeded on open.** Every unit the panel shows
 *    has a staged value from the moment it opens; the switches, radios and
 *    preview plate read that, never `unit.state` directly. `unit.state` is
 *    read live only for the D6 sub-line ("{partner} sees {Level}"), the
 *    Approve/Done footer rule and the submit's `changed` set — so a poll merge
 *    mid-run can flip a footer or a sub-line, which is acceptable: it is
 *    server truth, and staging is untouched by it.
 *
 * The panel owns no ledger state. Every read and every write goes through the
 * `host` interface (implemented by `SharingRegistry` and the chat ceremony
 * launcher), which is the consent API's seam. `requestClose(changedUnitIds)`
 * — always an array, possibly empty — is the SUCCESS close; `requestClose()`
 * with no argument is a dismiss (Done).
 *
 * @module sections/partnerSharing/SharingDetailPanel
 */

import { escapeHtml } from '../../core/utils.js';
import { PRIVATE_ROWS, renderStatePill } from '../../components/sharingConsentPrimitives.js';
import {
  preselectDepth,
  offeredDepths,
  seedStaged,
  frozenConsumerCount,
  subLineFor,
  queueScope,
  gestaltGroupId
} from './sharingLedgerModel.js';

/**
 * The `/library` destination for a frozen consumer's guide: straight to that
 * shared guide's detail when the consumer carries its `guideDefinitionId`
 * (`frozenConsumersForViewer`'s additive backend field), else the bare
 * `/library` fallback rather than a guessed link.
 * @param {string} [guideDefinitionId]
 * @returns {string}
 */
function libraryHref(guideDefinitionId) {
  return guideDefinitionId
    ? `/library?tab=guides&sharedGuide=${encodeURIComponent(guideDefinitionId)}`
    : '/library';
}

/**
 * Display labels for the ledger's `full|basic|off` depth vocabulary plus the
 * `review` pill — the switch segments, the radio rows' titles, the plate's
 * "At {Level}" and every pill's text.
 *
 * Lives here rather than in `sharingLedgerModel.js` because that module is
 * deliberately display-string-free (pure derivation over the view-model). The
 * registry reaches it through {@link renderSharingPill}, so there is exactly one
 * copy of these four words.
 *
 * @type {Record<'full'|'basic'|'off'|'review', string>}
 */
/** Every depth the switch and the radio rows render, in control order; an unoffered one renders disabled. */
const ALL_DEPTHS = ['off', 'basic', 'full'];

const DEPTH_LABELS = {
  full: 'Full',
  basic: 'Basic',
  off: 'Private',
  review: 'Needs review'
};

/** Depth ordering for the downgrade comparison (a frozen guide consumed more than the unit now offers). */
const DEPTH_RANK = { off: 0, basic: 1, full: 2 };

/**
 * The muted descriptor beside each depth row's title — a quick scent of the
 * level; the preview plate below shows the actual text.
 * @type {Record<'off'|'basic'|'full', string>}
 */
const DEPTH_DESCRIPTORS = {
  off: 'Nothing is shared',
  basic: 'General patterns',
  full: 'Adds what conversations confirmed'
};

/** Inline overview error when a unit in scope somehow reaches the submit with nothing staged. */
const INCOMPLETE_ERROR = "Some topics haven't been set yet. Choose a level for each topic before approving.";

/** Fallback copy when a single-unit consent write is rejected by the server. */
const COMMIT_ERROR = 'Could not save that choice. Please try again.';

/** Fallback copy when the run's batch submit is rejected without a message. */
const SUBMIT_ERROR = 'Could not save your choices. Please try again.';

/** How long the success checkmark holds before the panel closes itself. */
const SUBMIT_SUCCESS_MS = 500;

/**
 * The wizard's step-transition timing, matched exactly
 * (`BatchQuestionnaireOrchestrator.navigateToGroup`): outgoing content slides
 * out for 250 ms, then the new step slides in over 300 ms.
 */
const SLIDE_OUT_MS = 250;
const SLIDE_IN_MS = 300;

/**
 * The 16 px right chevron the overview rows and the "Where this is used"
 * toggle both carry — the registry rows' chevron path at the panel's scale.
 */
const CHEVRON_SVG = '<svg width="16" height="16" viewBox="0 0 16 16"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';

/** The three `contentType`s this component owns on the shared right panel. */
export const SHARING_CONTENT_TYPES = Object.freeze(['sharing-unit', 'sharing-queue', 'sharing-overview']);

/**
 * One ledger state pill, wrapped in an explicit BEM modifier so CSS can single
 * out `Review` (decision F: only Review keeps `notification-label`'s leading
 * dot, which is what makes "not yet decided" visually distinct).
 *
 * @param {'full'|'basic'|'off'|'review'} stateKey - Allowlisted pill key (from `pillFor`)
 * @returns {string} HTML string
 */
export function renderSharingPill(stateKey) {
  const key = DEPTH_LABELS[stateKey] ? stateKey : 'review';
  return `<span class="sharing-pill sharing-pill--${key}">${renderStatePill(key, DEPTH_LABELS[key])}</span>`;
}

/**
 * Format an ISO date (`YYYY-MM-DD`, or a full timestamp — only the date part
 * is read) as "Aug 2, 2026". Used for consumer freeze dates.
 *
 * Parsed field-by-field rather than through `new Date(iso)`: the bare-date form
 * parses as UTC midnight, which renders as the PREVIOUS day everywhere west of
 * Greenwich — a receipt date that silently shifts by a day is worse than none.
 *
 * @param {string} iso - `YYYY-MM-DD` or full ISO timestamp
 * @returns {string} Formatted date, or '' when unparseable
 */
function formatShareDate(iso) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!match) return '';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export class SharingDetailPanel {
  /**
   * @param {Object} config
   * @param {{emit: Function}} config.eventBus - App event bus
   * @param {Object} config.host - Ledger owner + panel-ownership bookkeeping. Must implement:
   *   `getLedger()`, `getPartnerName()`,
   *   `setUnitState(unitId, state)`, `approveUnit(unitId, state)`,
   *   `submitGroup(groupId, unitStates)`, `onLedgerChanged()`,
   *   `onPanelOpened(contentType, unitId)`, `requestClose(changedUnitIds?)`.
   */
  constructor({ eventBus, host }) {
    this.eventBus = eventBus;
    this.host = host;

    /** Monotonic per-emit token; a stale `onDispose` or write continuation compares against it and no-ops. */
    this._token = 0;
    /**
     * The shape currently rendered. `queue` is the whole run — overview and
     * unit phases both root under it, so `.sharing-panel--queue` matches
     * throughout the run.
     * @type {'single'|'queue'|null}
     */
    this._mode = null;
    /** @type {HTMLElement|null} The current phase's detached-then-mounted root. */
    this._root = null;
    /** @type {HTMLElement|null} The current phase's `titleNode` (mutated in place across steps). */
    this._titleEl = null;
    /** @type {string|null} */
    this._contentType = null;
    /** @type {string|null} Unit currently displayed (single mode, or the run's unit phase). */
    this._unitId = null;
    /**
     * The staging layer — every unit the panel shows, seeded on open
     * (`seedStaged`) and overwritten by switch/radio clicks. Nothing here has
     * been written to the server until a black button commits it.
     * @type {Record<string, 'full'|'basic'|'off'>}
     */
    this._pending = {};
    /** @type {'overview'|'unit'|null} Live run's phase; null when no run is open. */
    this._queuePhase = null;
    /**
     * Unit phase flavour: `true` while walking ("Review one by one" — Back /
     * Next / Finish with the progress bar, slides between steps), `false` for
     * a drill from an overview row (Done returns). A unit phase in queue mode
     * is only ever reached from the overview, so no "from" field is needed.
     * @type {boolean}
     */
    this._walking = false;
    /** @type {boolean} Set when Finish returns to the overview; drives the D5 subtitle. Reset only in `_resetMode`. */
    this._walkCompleted = false;
    /** @type {string|null} The single group this run covers. */
    this._queueGroupId = null;
    /** @type {Array<string>|null} Run traversal order (unit ids, never snapshots). */
    this._queueIds = null;
    /**
     * Every unit id in the run's group — the batch the submit must carry
     * (`submitConsent` requires the whole disclosure set). `_queueIds` is the
     * subset the overview shows and the walk visits: only the topics still
     * needing review when some are already decided, else all of them.
     * @type {Array<string>|null}
     */
    this._queueAllIds = null;
    this._queueIndex = 0;
    /**
     * Unit ids whose "Where this is used" section is expanded (one expander
     * per unit — all consumer detail reveals in a single click). Survives
     * repaints within a mode and queue back/forward; cleared on reset.
     * @type {Set<string>}
     */
    this._usedExpanded = new Set();
    /** True while a host write is in flight — every control renders disabled. */
    this._busy = false;
    /** @type {string} Verbatim server message from a rejected submit; rendered inline on the overview, cleared on retry. */
    this._submitError = '';
    /** @type {number|null} Success-hold timer; cleared on any teardown. */
    this._submitTimer = null;
    /**
     * Step-transition timers (ghost removal + slide-in cleanup, see
     * `_paintQueueAnimated`). Cleared on any new transition, on a phase
     * re-root and on `_resetMode`; the transition is purely cosmetic, so a
     * cleared timer only ever truncates an animation, never loses state.
     * @type {Array<number>}
     */
    this._slideTimers = [];

    this._onRootClick = this._onRootClick.bind(this);
  }

  /* ------------------------------------------------------------------ *
   * Ledger reads                                                        *
   * ------------------------------------------------------------------ */

  /** @returns {Object} The live ledger (owned by the host). */
  _ledger() {
    return this.host.getLedger() || { masters: {}, groups: [], units: [] };
  }

  /** @returns {Array<Object>} Live units. */
  _units() {
    return this._ledger().units || [];
  }

  /**
   * @param {string} unitId
   * @returns {Object|null} The live unit row — re-read on every paint, never snapshotted.
   */
  _unit(unitId) {
    return this._units().find(u => u.id === unitId) || null;
  }

  /**
   * @param {string} groupId
   * @returns {Object|null}
   */
  _group(groupId) {
    return (this._ledger().groups || []).find(g => g.id === groupId) || null;
  }

  /**
   * The ids in scope whose staged value differs from their committed state —
   * the one source for the overview's Approve/Done rule, the D6 sub-line and
   * the batch submit's `changed` set. Ids no longer in the ledger are skipped.
   *
   * @returns {Array<string>}
   */
  _pendingIds() {
    const ids = this._mode === 'queue' ? (this._queueIds || []) : (this._unitId ? [this._unitId] : []);
    return ids.filter(id => {
      const unit = this._unit(id);
      return unit && this._pending[id] !== unit.state;
    });
  }

  /* ------------------------------------------------------------------ *
   * Mode lifecycle                                                      *
   * ------------------------------------------------------------------ */

  /**
   * Drop every mode-local field, staged consent included — this is
   * ABANDONMENT, and abandoning a run discards its whole batch (no draft
   * layer, no confirm prompt: an owner-ratified divergence from the
   * questionnaire's localStorage draft). Safe to call when no mode is live.
   *
   * It also ADVANCES THE TOKEN, which is what makes a bare close — no reopen
   * behind it — kill a write's late continuations: without that bump, a batch
   * or single approve resolving after the user closed the panel would still
   * run its success path against whatever the panel shows next.
   */
  _resetMode() {
    // Detach the discarded root's delegated listener so a root the panel
    // manager still briefly references (a delayed cross-fade swap landing
    // after close) is inert and doesn't retain this component through it.
    this._root?.removeEventListener('click', this._onRootClick);
    if (this._submitTimer) {
      clearTimeout(this._submitTimer);
      this._submitTimer = null;
    }
    this._clearSlideTimers();
    this._token += 1;
    this._mode = null;
    this._root = null;
    this._titleEl = null;
    this._contentType = null;
    this._unitId = null;
    this._pending = {};
    this._queuePhase = null;
    this._walking = false;
    this._walkCompleted = false;
    this._queueGroupId = null;
    this._queueIds = null;
    this._queueAllIds = null;
    this._queueIndex = 0;
    this._usedExpanded = new Set();
    this._busy = false;
    this._submitError = '';
  }

  /**
   * Build this mode's fresh detached root (rule 1 in the file docblock) and take
   * ownership of it. Never reuse a root across modes or phases.
   *
   * @param {'single'|'queue'} mode
   * @param {string} contentType
   * @returns {HTMLElement}
   */
  _beginMode(mode, contentType) {
    this._mode = mode;
    this._contentType = contentType;
    const root = document.createElement('div');
    root.className = `sharing-panel sharing-panel--${mode}`;
    root.addEventListener('click', this._onRootClick);
    this._root = root;
    return root;
  }

  /**
   * Move a LIVE run onto a fresh root for its next phase (overview ⇄ unit,
   * which change `contentType`). Rule 1 still applies — a new root, a new emit
   * — but the run's staging must survive, so this deliberately does NOT call
   * `_resetMode()`; it only detaches the outgoing root's listener and drops
   * any slide timers still aimed at it.
   *
   * @param {'queue'} mode
   * @param {string} contentType
   */
  _switchPhaseRoot(mode, contentType) {
    this._root?.removeEventListener('click', this._onRootClick);
    this._clearSlideTimers();
    this._beginMode(mode, contentType);
  }

  /**
   * Emit `right-panel:open` for the phase currently being built.
   * @param {Object} extra - Extra payload fields (`titleNode` / `titleAlign` / `backButton`)
   */
  _emit(extra = {}) {
    const token = ++this._token;
    this.host.onPanelOpened(this._contentType, this._unitId);
    this.eventBus.emit('right-panel:open', {
      ...extra,
      content: this._root,
      contentType: this._contentType,
      onDispose: () => {
        // Stale disposer: the panel already moved on to a later phase, whose
        // state must survive. (RightPanelManager fires the OUTGOING payload's
        // disposer after the incoming one has been recorded.)
        if (this._token !== token) return;
        this._resetMode();
      }
    });
  }

  /** Called by the host when a `right-panel:closed`/foreign open retires our panel. */
  handleClosed() {
    this._resetMode();
  }

  /**
   * Whether any consent surface is open — every open mode holds staging now,
   * single included. The section asks before letting a background poll
   * re-render (which would remount the registry and take the staging with it).
   *
   * @returns {boolean}
   */
  hasActiveRun() {
    return this._mode != null;
  }

  /** Tear down: the host owns emitting the actual close. */
  destroy() {
    this._resetMode();
  }

  /**
   * Take ownership of a fresh centred title node — the header pill every
   * phase shares. Built per emit, because the panel manager owns the previous
   * one's slot.
   * @returns {HTMLElement}
   */
  _newTitleNode() {
    this._titleEl = document.createElement('div');
    this._titleEl.className = 'wizard-question-number sharing-queue__progress';
    return this._titleEl;
  }

  /**
   * Set the centred header pill — the one title mechanism every phase uses.
   * @param {string} label
   */
  _setTitlePill(label) {
    if (this._titleEl) {
      this._titleEl.innerHTML = label
        ? `<span class="wizard-question-number__pill">${escapeHtml(label)}</span>`
        : '';
    }
  }

  /* ------------------------------------------------------------------ *
   * Single mode                                                         *
   * ------------------------------------------------------------------ */

  /**
   * Open one unit's detail with its staged value seeded. A row click always
   * lands here — never a 1-item run. The header pill names the counterpart
   * ("Sharing with {partner}") — a recorded divergence from the style guide's
   * empty-title rule, because the body's topic title cannot carry that.
   * @param {string} unitId
   */
  openSingle(unitId) {
    const unit = this._unit(unitId);
    if (!unit) return;
    this._resetMode();
    this._beginMode('single', 'sharing-unit');
    this._unitId = unitId;
    this._pending[unitId] = seedStaged(unit);
    this._newTitleNode();
    this._setTitlePill(`Sharing with ${this.host.getPartnerName()}`);
    this._paintSingle();
    this._emit({ titleNode: this._titleEl, titleAlign: 'center' });
  }

  /**
   * Paint single mode in place: the shared detail body over its own footer —
   * **Approve & share** while the staged value differs from the committed
   * state, else **Done** (a dismiss). Same body shell as the run so one CSS
   * treatment anchors the footer in both modes.
   */
  _paintSingle() {
    const unit = this._unit(this._unitId);
    if (!unit || !this._root) return;
    const footer = this._pending[unit.id] !== unit.state
      ? this._footerHtml({ nextLabel: 'Approve & share', nextAction: 'sharing-single-approve', progress: false, onlyNext: true })
      : this._footerHtml({ nextLabel: 'Done', nextAction: 'sharing-single-done', progress: false, onlyNext: true });
    this._root.innerHTML = this._queueBodyHtml(this._unitBodyHtml(unit)) + footer;
  }

  /* ------------------------------------------------------------------ *
   * Queue run — entry + overview                                        *
   * ------------------------------------------------------------------ */

  /**
   * "Review N topics" / "Review all" — open a run over ONE group's disclosure
   * set. The overview shows (and the walk visits) only the topics still
   * needing a first look while any exist — the count the banner promised —
   * and every topic once none does. Every unit in the group is seeded and
   * stays in the batch either way; nothing is written until the overview's
   * Approve & share.
   *
   * @param {string} [groupId] - Defaults to the sole gestalt group
   */
  openQueue(groupId = gestaltGroupId(this._ledger().groups)) {
    const all = queueScope(this._units(), groupId);
    if (!all.length) return;
    this._resetMode();
    this._beginMode('queue', 'sharing-overview');
    this._queueGroupId = groupId;
    // Show and walk only what still needs a first look, so "Review 4 topics"
    // opens on four rows. Decided topics stay out of sight (they are
    // individually reachable from the registry) but stay IN the batch: the
    // submit carries the whole group, re-committing them unchanged. Once
    // nothing needs review the run covers every topic, as the final review.
    const undecided = all.filter(id => this._unit(id)?.state == null);
    this._queueAllIds = all;
    this._queueIds = undecided.length ? undecided : all;
    this._queueIndex = 0;
    for (const id of all) {
      const unit = this._unit(id);
      if (unit) this._pending[id] = seedStaged(unit);
    }
    this._queuePhase = 'overview';
    this._newTitleNode();
    this._paintOverview();
    this._emit({ titleNode: this._titleEl, titleAlign: 'center' });
  }

  /**
   * Paint the overview: title, the D5 state subtitle, any inline submit
   * error, one row per topic (name + sub-line — the readiness note, else the
   * D6 pending-change line — the `.partner-seg` switch with all three depths
   * and the unoffered ones disabled, a right chevron), the "How sharing works"
   * link under the last row, then the D4 footer.
   *
   * The whole row `div` carries `sharing-overview-open`; the switch's buttons
   * carry `sharing-overview-stage` and win because `_onRootClick` resolves the
   * innermost `[data-action]` — no `stopPropagation` anywhere (it would break
   * document-level dismiss listeners such as the filter menu's). The name
   * `<button>` carries focus only. Rows whose unit left the ledger are
   * skipped; the paint never guards on the group existing (a poll can briefly
   * replace `groups`) — only the footer's confirmed check reads it, null-safe.
   */
  _paintOverview() {
    if (!this._root) return;
    const partner = this.host.getPartnerName();
    const showApprove = this._pendingIds().length > 0 || !this._group(this._queueGroupId)?.confirmed;

    // One short line per state; the switches, the button and the chevrons
    // already show the levels, the commit and the tap, so the sentence only
    // names the two paths.
    let subtitle;
    if (!showApprove) subtitle = `Every topic is set. Tap a topic to see what ${escapeHtml(partner)} gets:`;
    else if (this._walkCompleted) subtitle = 'Approve your selections, or tap a topic to change one:';
    // Every topic already decided, confirm still owed (master off→on, or the
    // registry's "Review all"): nothing here is a suggestion.
    else if ((this._queueAllIds || []).every(id => this._unit(id)?.state != null)) subtitle = `Approve to share these with ${escapeHtml(partner)}, or tap a topic to change one:`;
    else subtitle = 'Approve the suggested levels, or tap a topic to review it first:';

    const rows = (this._queueIds || []).map(id => this._unit(id)).filter(Boolean).map(unit => {
      const staged = this._pending[unit.id];
      // One sub-line slot: a readiness/revision note first (why some
      // segments are disabled), else the D6 pending-change line. They cannot
      // coincide — readiness notes apply to undecided topics, D6 to decided ones.
      const note = subLineFor(unit);
      let subLine = '';
      if (note) subLine = `<small>${escapeHtml(note.text)}</small>`;
      else if (unit.state != null && staged !== unit.state) subLine = `<small>${escapeHtml(partner)} sees ${DEPTH_LABELS[unit.state]}</small>`;
      const offered = offeredDepths(unit);
      // Every switch has all three segments so the column stays aligned; a
      // depth this topic does not offer renders disabled, the sub-line says why.
      const segments = ALL_DEPTHS.map(depth => {
        const on = staged === depth;
        const unavailable = !offered.includes(depth);
        return `<button type="button" class="partner-seg__btn${on ? ' partner-seg__btn--on' : ''}"
                        data-action="sharing-overview-stage" data-state="${depth}"
                        aria-pressed="${on ? 'true' : 'false'}" aria-disabled="${unavailable ? 'true' : 'false'}"${unavailable || this._busy ? ' disabled' : ''}>${DEPTH_LABELS[depth]}</button>`;
      }).join('');
      return `
        <div class="sharing-overview__row" data-action="sharing-overview-open" data-unit-id="${escapeHtml(unit.id)}">
          <button type="button" class="sharing-overview__name">${escapeHtml(unit.name)}${subLine}</button>
          <span class="partner-seg" data-unit-id="${escapeHtml(unit.id)}">${segments}</span>
          <span class="sharing-overview__chevron" aria-hidden="true">${CHEVRON_SVG}</span>
        </div>`;
    }).join('');

    const footer = showApprove
      ? this._footerHtml({
        nextLabel: 'Approve & share',
        nextAction: 'sharing-overview-approve',
        progress: false,
        onlyNext: true,
        trailer: '<button type="button" class="sharing-overview__walk" data-action="sharing-overview-walk">Review one by one</button>'
      })
      : this._footerHtml({ nextLabel: 'Done', nextAction: 'sharing-overview-done', progress: false, onlyNext: true });

    this._root.innerHTML = this._queueBodyHtml(`
      <h3 class="sharing-overview__title">What ${escapeHtml(partner)} sees</h3>
      <p class="sharing-overview__subtitle"><span class="sharing-overview__subtitle-text">${subtitle}</span></p>
      ${this._submitError ? `<p class="sharing-overview__error" role="alert">${escapeHtml(this._submitError)}</p>` : ''}
      <div class="sharing-overview__list">${rows}</div>
      <p class="sharing-overview__how"><button type="button" class="sharing-overview__how-link" data-action="sharing-overview-intro">How sharing works<span class="sharing-overview__how-chevron" aria-hidden="true">${CHEVRON_SVG}</span></button></p>`)
      + footer;
    this._setTitlePill(`Sharing with ${partner}`);
  }

  /**
   * "How sharing works" — the old instruction step, now a page of its own
   * inside the run, reached from the overview's link (never as a forced first
   * step): the why-share lede, the three-step mechanic as it works today, the
   * never-shared promise (`PRIVATE_ROWS`, the pre-link consent screen's exact
   * words), and a short FAQ on the shared `.faq` component. Re-roots like a
   * drill (back arrow + "Got it" both return to the overview); staging is
   * untouched.
   */
  _enterIntro() {
    if (this._mode !== 'queue' || this._queuePhase !== 'overview') return;
    this._queuePhase = 'intro';
    this._unitId = null;
    this._switchPhaseRoot('queue', 'sharing-queue');
    this._newTitleNode();
    this._paintIntro();
    this._emit({
      titleNode: this._titleEl,
      titleAlign: 'center',
      backButton: { onClick: () => this._enterOverview() }
    });
  }

  /** Paint the intro page in place. */
  _paintIntro() {
    if (!this._root) return;
    const partner = escapeHtml(this.host.getPartnerName());
    const steps = [
      'Review each topic.',
      'Choose how much to share — full, basic, or private.',
      `Nothing reaches ${partner} without your approval.`
    ];
    const stepsHtml = steps.map((text, i) => `
      <li class="sharing-instruction__step">
        <span class="sharing-instruction__step-num" aria-hidden="true">${i + 1}</span>
        <span class="sharing-instruction__step-text">${text}</span>
      </li>`).join('');
    const neverHtml = PRIVATE_ROWS.map(row => `<li>${escapeHtml(row)}</li>`).join('');
    const faq = [
      { q: 'What’s the difference between Basic and Full?',
        a: `Basic shares the general pattern. Full adds what your conversations confirmed — the specifics that make ${partner}’s guidance fit you both. You see the exact words for each before you choose.` },
      { q: 'Can I change my mind later?',
        a: `Yes. Open any topic from the Sharing list and pick a different level; ${partner}’s guidance updates from then on. A guide that was already created keeps the version it was built from.` },
      { q: `What does ${partner} see if I pick Private?`,
        a: `Nothing from that topic. ${partner}’s guidance simply won’t draw on it.` }
    ];
    const faqHtml = faq.map((item, idx) => `
      <div class="faq__item">
        <button type="button" class="faq__question" data-action="sharing-intro-faq" data-faq="${idx}">
          <span>${item.q}</span>
          <span class="faq__icon">+</span>
        </button>
        <div class="faq__answer" data-faq-answer="${idx}"><p>${item.a}</p></div>
      </div>`).join('');

    this._root.innerHTML = this._queueBodyHtml(`
      <div class="sharing-queue__instruction">
        <h3 class="sharing-instruction__title">Sharing with ${partner}</h3>
        <p class="sharing-instruction__body">When you share, ${partner}'s guidance reflects <b>your side of things</b> too — so it fits you both, not just one of you. The more you share, the better Encouple can support your relationship.</p>
        <h4 class="sharing-instruction__section-title">How it works</h4>
        <ol class="sharing-instruction__steps">${stepsHtml}</ol>
        <div class="sharing-instruction__never">
          <p class="sharing-instruction__never-label">Never shared</p>
          <ul class="sharing-instruction__never-list">${neverHtml}</ul>
        </div>
        <div class="faq sharing-instruction__faq">
          <h4 class="faq__title">Common questions</h4>
          <div class="faq__list">${faqHtml}</div>
        </div>
      </div>`)
      + this._footerHtml({ nextLabel: 'Got it', nextAction: 'sharing-intro-done', progress: false, onlyNext: true });
    this._setTitlePill('How sharing works');
  }

  /**
   * One FAQ item's open/closed state, toggled in place (a repaint would reset
   * every item) — the same class + glyph swap `PartnerSection._toggleFaq` does
   * for the shared `.faq` component.
   * @param {HTMLElement} question - The clicked `.faq__question`
   */
  _toggleIntroFaq(question) {
    const item = question.closest('.faq__item');
    if (!item) return;
    const open = item.classList.toggle('faq__item--open');
    const icon = question.querySelector('.faq__icon');
    if (icon) icon.textContent = open ? '−' : '+';
  }

  /* ------------------------------------------------------------------ *
   * Queue run — unit phase                                              *
   * ------------------------------------------------------------------ */

  /**
   * Repaint the live run phase in place: the overview, or the unit phase
   * (shared detail body + the walk's progress/Back/Next|Finish footer, or the
   * drill's Done) with the "Topic n of N" header pill.
   */
  _paintQueue() {
    if (!this._root) return;
    if (this._queuePhase === 'intro') {
      this._paintIntro();
      return;
    }
    if (this._queuePhase === 'overview') {
      this._paintOverview();
      return;
    }
    const unit = this._unit(this._unitId);
    if (!unit) return;
    const total = this._queueIds.length;
    const index = this._queueIndex;
    const last = index === total - 1;

    const footer = this._walking
      ? this._footerHtml({ nextLabel: last ? 'Finish' : 'Next' })
      : this._footerHtml({ nextLabel: 'Done', nextAction: 'sharing-overview-back', progress: false, onlyNext: true });

    this._root.innerHTML = this._queueBodyHtml(this._unitBodyHtml(unit)) + footer;
    this._setProgress(total > 1 ? index / (total - 1) : 1);
    this._setTitlePill(`Topic ${index + 1} of ${total}`);
  }

  /**
   * Wrap a phase body in the panel shell's scrolling region — the flex column
   * that keeps the footer anchored at the panel's bottom edge
   * (`features/partner-sharing.css`). Both modes use it, so the slide
   * transition's `.sharing-queue__body` selectors hold everywhere.
   *
   * @param {string} inner - The phase's body HTML
   * @returns {string} HTML
   */
  _queueBodyHtml(inner) {
    return `<div class="sharing-queue__body">${inner}</div>`;
  }

  /**
   * The one footer implementation — the promoted wizard chrome
   * (`components/wizard-chrome.css`) with `data-action` hooks, never the
   * hardcoded `id`s `BatchQuestionnaireOrchestrator` looks its own nodes up
   * by. Two shapes:
   *
   * - **walk** (default): progress bar + Back + the forward button.
   * - **`onlyNext`**: no Back, no progress — the overview's Approve & share /
   *   Done, the drill's Done and single mode's Approve & share / Done. The
   *   footer then carries the `sharing-queue__footer--overview` modifier,
   *   which is the CSS hook for the full-width ink button
   *   (`.sharing-queue__footer--overview .wizard-nav-button`); the button
   *   classes themselves never change. `trailer` renders after the button
   *   container (the overview's quiet "Review one by one" link).
   *
   * `.wizard-button-container` is always present — `_paintQueueAnimated`
   * fades it during a step slide.
   *
   * @param {Object} opts
   * @param {string} opts.nextLabel - Copy for the forward button
   * @param {string} [opts.nextAction='sharing-queue-next'] - Its `data-action`
   * @param {boolean} [opts.progress=true] - Render the progress bar
   * @param {boolean} [opts.onlyNext=false] - One-button shape (no Back)
   * @param {string} [opts.trailer=''] - HTML rendered after the button container
   * @returns {string} HTML
   */
  _footerHtml({ nextLabel, nextAction = 'sharing-queue-next', progress = true, onlyNext = false, trailer = '' }) {
    const disabled = this._busy ? ' disabled' : '';
    return `
      <div class="wizard-footer sharing-queue__footer${onlyNext ? ' sharing-queue__footer--overview' : ''}">
        ${progress ? `<div class="wizard-footer__progress">
          <div class="wizard-footer__progress-fill"></div>
        </div>` : ''}
        <div class="wizard-button-container">
          ${onlyNext ? '' : `<button type="button" class="wizard-nav-button wizard-nav-button--back"
                  data-action="sharing-queue-back"${disabled}>Back</button>`}
          <button type="button" class="wizard-nav-button"
                  data-action="${nextAction}"${disabled}>${escapeHtml(nextLabel)}</button>
        </div>
        ${trailer}
      </div>
    `;
  }

  /**
   * Repaint the unit phase for a STEP CHANGE with the questionnaire's slide
   * transition (out left / in from right going forward, mirrored going back —
   * the shared `slide-out-*`/`slide-in-*` classes and timings from
   * `components/wizard-chrome.css`, exactly `BatchQuestionnaireOrchestrator
   * .navigateToGroup`'s sequence).
   *
   * State is ALREADY current when this runs and the repaint below is
   * synchronous — the animation is a purely cosmetic layer on top. The
   * OUTGOING body is cloned into an inert ghost (pointer-events off, every
   * `data-action` hook stripped, so neither clicks nor the panel's own
   * queries can ever land on it) that plays the slide-out over the panel,
   * while the freshly painted step waits hidden (`--entering`) and then plays
   * the slide-in; the footer buttons ride the wizard's own fade. Keeping the
   * navigation synchronous is deliberate — the wizard's async approach would
   * put consent staging mid-flight for the length of every animation, and
   * every existing invariant (and test) of this run assumes a step change
   * lands atomically.
   *
   * A navigation landing mid-transition clears the previous ghost and timers
   * rather than stacking; a plain repaint mid-transition (a depth click)
   * truncates the effect harmlessly (the closure's nodes detach and the class
   * ops no-op). Falls back to a plain repaint when there is no body yet.
   *
   * @private
   * @param {'forward'|'back'} direction
   */
  _paintQueueAnimated(direction) {
    const oldBody = this._root?.querySelector('.sharing-queue__body:not(.sharing-queue__body--ghost)');
    if (!oldBody) {
      this._paintQueue();
      return;
    }

    this._clearSlideTimers();
    this._root.querySelectorAll('.sharing-queue__body--ghost').forEach(g => g.remove());

    const outClass = direction === 'back' ? 'slide-out-right' : 'slide-out-left';
    const inClass = direction === 'back' ? 'slide-in-left' : 'slide-in-right';

    // Measured before the repaint destroys the outgoing body. The inline
    // height keeps the absolute ghost from overhanging the footer.
    const height = oldBody.offsetHeight;
    const scrollTop = oldBody.scrollTop;
    const ghost = oldBody.cloneNode(true);
    // A ghost cloned mid-transition may carry the hidden marker — it must be
    // visible while it slides out.
    ghost.classList.remove('sharing-queue__body--entering');
    ghost.classList.add('sharing-queue__body--ghost', outClass);
    if (height) ghost.style.height = `${height}px`;
    ghost.querySelectorAll('[data-action]').forEach(el => el.removeAttribute('data-action'));

    this._paintQueue();

    const newBody = this._root.querySelector('.sharing-queue__body');
    const buttons = this._root.querySelector('.wizard-button-container');
    newBody?.classList.add('sharing-queue__body--entering');
    buttons?.classList.add('wizard-button-container--faded');
    this._root.appendChild(ghost);
    ghost.scrollTop = scrollTop;

    this._slideTimers.push(setTimeout(() => {
      ghost.remove();
      newBody?.classList.remove('sharing-queue__body--entering');
      newBody?.classList.add(inClass);
      buttons?.classList.remove('wizard-button-container--faded');
      this._slideTimers.push(setTimeout(() => {
        newBody?.classList.remove(inClass);
      }, SLIDE_IN_MS));
    }, SLIDE_OUT_MS));
  }

  /** Clear the step-transition timers. Safe with none pending. @private */
  _clearSlideTimers() {
    for (const timer of this._slideTimers) clearTimeout(timer);
    this._slideTimers = [];
  }

  /**
   * Set the footer's progress width. Runtime state, not styling — a property
   * rather than an inline `style=` attribute in the markup. No-op when the
   * footer shape has no bar.
   * @param {number} ratio - 0..1
   */
  _setProgress(ratio) {
    const fill = this._root?.querySelector('.wizard-footer__progress-fill');
    if (fill) fill.style.width = `${Math.round(ratio * 100)}%`;
  }

  /* ------------------------------------------------------------------ *
   * Queue run — navigation                                              *
   * ------------------------------------------------------------------ */

  /**
   * "Review one by one" — start the walk at the first topic, always from the
   * top, whatever was already staged.
   */
  _enterWalk() {
    if (this._mode !== 'queue' || this._queuePhase !== 'overview') return;
    this._walking = true;
    this._enterUnitPhase(0);
  }

  /**
   * An overview row was tapped — drill into that one topic (Done returns to
   * the overview).
   * @param {string} unitId
   */
  _openFromOverview(unitId) {
    if (this._mode !== 'queue' || this._queuePhase !== 'overview') return;
    const index = (this._queueIds || []).indexOf(unitId);
    if (index < 0) return;
    this._walking = false;
    this._enterUnitPhase(index);
  }

  /**
   * Back — pure navigation, never a mutation, and never a discard: staged
   * choices survive every move. Earlier step, or from step 1 the overview.
   */
  _queueBack() {
    if (this._mode !== 'queue' || this._queuePhase !== 'unit') return;
    if (this._queueIndex === 0) {
      this._enterOverview();
      return;
    }
    this._enterUnitPhase(this._queueIndex - 1, 'back');
  }

  /**
   * Next / Finish — pure navigation too: every step's value is already
   * staged (seeded on open, overwritten by clicks), so nothing is frozen on
   * the way out. Finish marks the walk complete and lands on the overview,
   * which then reads as the final review.
   */
  _queueNext() {
    if (this._mode !== 'queue' || this._queuePhase !== 'unit') return;
    if (this._queueIndex < this._queueIds.length - 1) {
      this._enterUnitPhase(this._queueIndex + 1, 'forward');
      return;
    }
    this._walkCompleted = true;
    this._enterOverview();
  }

  /**
   * Return to the overview. Re-roots (the `contentType` changes) but is NOT a
   * mode restart — `_resetMode` would take the whole staged batch with it.
   *
   * Idempotent: the header's back arrow stays wired for the ~130 ms of
   * `RightPanelManager`'s cross-fade, so a second press must not re-root the
   * overview over itself.
   */
  _enterOverview() {
    if (this._mode !== 'queue' || this._queuePhase === 'overview') return;
    this._queuePhase = 'overview';
    this._unitId = null;
    this._switchPhaseRoot('queue', 'sharing-overview');
    this._newTitleNode();
    this._paintOverview();
    this._emit({ titleNode: this._titleEl, titleAlign: 'center' });
  }

  /**
   * Enter (or move within) the unit phase at a given index. Coming from the
   * overview changes `contentType`, so that path re-roots the run — a fresh
   * emit that rides `RightPanelManager`'s own cross-fade and carries the
   * header back arrow (→ overview). Within the phase, `direction` drives the
   * in-place step slide.
   *
   * @param {number} index
   * @param {'forward'|'back'|null} [direction] - Slide the step change
   *   (`_paintQueueAnimated`); null paints plainly.
   */
  _enterUnitPhase(index, direction = null) {
    const reroot = this._queuePhase === 'overview';
    this._queuePhase = 'unit';
    this._queueIndex = index;
    this._unitId = this._queueIds[index];
    if (reroot) {
      // Leaving the overview retires its inline submit error: the user is
      // now changing the batch the message was about.
      this._submitError = '';
      this._switchPhaseRoot('queue', 'sharing-queue');
      this._newTitleNode();
      this._paintQueue();
      this._emit({
        titleNode: this._titleEl,
        titleAlign: 'center',
        backButton: { onClick: () => this._enterOverview() }
      });
      return;
    }
    this.host.onPanelOpened(this._contentType, this._unitId);
    if (direction) this._paintQueueAnimated(direction);
    else this._paintQueue();
  }

  /* ------------------------------------------------------------------ *
   * Commits                                                             *
   * ------------------------------------------------------------------ */

  /**
   * Run one unit-shaped host seam under the async contract (rule 3 in the file
   * docblock), token-gated (rule 2). A seam returning no thenable (the fixture
   * harness) is treated as already-settled, which keeps that path synchronous.
   *
   * On settle, the ledger-side effects are UNGATED — the row rollback (on
   * failure), `host.onLedgerChanged()` and the error toast land whatever the
   * panel shows now, because server truth and a refused-write message belong
   * to no one run. Everything panel-side — `_busy`, `after`, `onFail`, the
   * repaint — runs only while the token still matches: a late single approve
   * must never close or repaint a successor run.
   *
   * @param {Object} unit - The live unit being written
   * @param {Function} seam - Zero-arg host call; returns a promise or null
   * @param {Function} [after] - Success continuation; defaults to a repaint
   * @param {Function} [onFail] - Failure continuation, run after the rollback
   *   and before the repaint (e.g. re-seeding the staged value)
   */
  _commitUnit(unit, seam, after, onFail) {
    // The exact fields the seams write — restored verbatim if the write fails.
    const prior = { state: unit.state, revisionReverted: unit.revisionReverted };
    const token = this._token;
    const settled = () => {
      this.host.onLedgerChanged();
      if (this._token !== token) return;
      this._busy = false;
      if (after) after();
      else this._repaint();
    };
    let pending;
    try {
      pending = seam();
    } catch (err) {
      this._commitFailed(unit, prior, err, onFail, token);
      return;
    }
    if (!pending || typeof pending.then !== 'function') {
      settled();
      return;
    }
    this._busy = true;
    this._repaint();
    pending.then(
      settled,
      (err) => this._commitFailed(unit, prior, err, onFail, token)
    );
  }

  /**
   * Roll a rejected write back to the values the row carried before it, and say
   * so — a consent control that silently keeps a value the server refused is
   * the one failure mode this surface cannot have. Rollback, registry repaint
   * and toast are ungated; `_busy`, `onFail` and the panel repaint are gated
   * on the token (see `_commitUnit`).
   *
   * @param {Object} unit
   * @param {{state: *, revisionReverted: *}} prior
   * @param {Object} err
   * @param {Function|undefined} onFail
   * @param {number} token - The emit token captured before the seam ran
   */
  _commitFailed(unit, prior, err, onFail, token) {
    Object.assign(unit, prior);
    this.host.onLedgerChanged();
    window.notificationManager?.showError(err?.error || COMMIT_ERROR);
    if (this._token !== token) return;
    this._busy = false;
    if (onFail) onFail();
    this._repaint();
  }

  /**
   * Single mode's Approve & share: commit the staged depth for this one unit.
   * A never-reviewed unit commits through `approveUnit` (the R5 approve event,
   * clearing any revision flag); a decided unit commits through `setUnitState`
   * (the W5 edit-while-live path).
   *
   * EXCEPT when this approval decides the LAST undecided topic of a group whose
   * confirm is still owed: the user has now seen every topic one at a time, so
   * the write goes through `submitGroup` with the whole set (this unit at the
   * staged depth, the rest at their committed states). That stamps the group
   * confirm — the thing the gestalt build waits on — from the final item, so
   * the banner never asks for a second "Approve" over choices already made.
   * An edit to a decided unit never takes this path: after master off→on the
   * confirm is owed for a fresh look at the set, not earned by one edit.
   *
   * On success the panel closes itself as a SUCCESS close (`requestClose([id])`
   * — the registry animates that row) and a toast confirms; nothing reads
   * panel state after the close. On failure the row is already rolled back to
   * `prior`, so the staged value is re-seeded from it (server truth) — the
   * radio lands on the committed depth, the plate follows, and the footer
   * reads Done beside the error toast.
   */
  _approveSingle() {
    const unit = this._unit(this._unitId);
    if (!unit || this._mode !== 'single' || this._busy) return;
    const depth = this._pending[unit.id];
    if (depth == null || depth === unit.state) return;
    const groupIds = queueScope(this._units(), unit.groupId);
    const completesSet = unit.state == null
      && !this._group(unit.groupId)?.confirmed
      && groupIds.every(id => id === unit.id || this._unit(id)?.state != null);
    let seam;
    if (completesSet) {
      const unitStates = {};
      for (const id of groupIds) unitStates[id] = id === unit.id ? depth : this._unit(id).state;
      seam = () => this.host.submitGroup(unit.groupId, unitStates);
    } else if (unit.state == null) {
      seam = () => this.host.approveUnit(unit.id, depth);
    } else {
      seam = () => this.host.setUnitState(unit.id, depth);
    }
    this._commitUnit(
      unit,
      seam,
      () => {
        this.host.requestClose([unit.id]);
        window.notificationManager?.showSuccess('Your sharing choice is saved.');
      },
      () => {
        this._pending[unit.id] = seedStaged(unit);
      }
    );
  }

  /**
   * The run's ONE write: every staged state for the group, submitted together
   * from the overview. Disabled in flight, spinner while it travels, checkmark
   * on the way out, and the panel closes itself — the questionnaire's submit
   * sequence, minus its hardcoded node ids.
   *
   * A submit whose values all equal the committed states is still meaningful:
   * it stamps this member's group confirm (`changed` is then `[]`).
   *
   * A rejection returns to the overview with the batch still staged and shows
   * the server's message verbatim, inline and as a toast: it names the unit
   * that failed, which is the only thing that makes a six-unit rejection
   * actionable.
   */
  _approveRun() {
    const groupId = this._queueGroupId;
    if (!groupId || this._busy || this._queuePhase !== 'overview') return;
    // The WHOLE group, not just the rows on screen: decided topics the
    // overview hid ride along at their committed (seeded) state.
    const units = (this._queueAllIds || []).map(id => this._unit(id)).filter(Boolean);
    // Local completeness gate: only reachable if the ledger grew mid-run (a
    // unit no seed ever staged). The backend would refuse the batch anyway —
    // this turns that round-trip into an immediate in-panel message.
    if (units.some(unit => this._pending[unit.id] == null)) {
      this._submitError = INCOMPLETE_ERROR;
      this._repaint();
      return;
    }
    const unitStates = {};
    for (const unit of units) unitStates[unit.id] = this._pending[unit.id];
    // Computed HERE, before the response overwrites the rows: which rows the
    // ledger list should animate once the panel is out of the way.
    const changed = this._pendingIds();

    const token = this._token;
    this._submitError = '';
    this._busy = true;
    this._renderSubmitPanel('spinner');

    let pending;
    try {
      pending = this.host.submitGroup(groupId, unitStates);
    } catch (err) {
      this._submitFailed(token, err);
      return;
    }
    if (!pending || typeof pending.then !== 'function') {
      this._submitSucceeded(token, changed);
      return;
    }
    pending.then(
      () => this._submitSucceeded(token, changed),
      (err) => this._submitFailed(token, err)
    );
  }

  /**
   * The submit landed. The canonical payload is already installed — that
   * happens token-independently in the host seam
   * (`PartnerSection._commitSharingSubmit`), because the server's answer must
   * land whether or not anyone is still looking at this panel.
   *
   * EVERYTHING HERE IS GATED, and the gate comes first. `_busy`, the root's
   * markup, the registry repaint and the close timer all belong to whichever
   * run currently owns the panel: a stale batch that cleared `_busy` would
   * re-enable the successor's controls in the middle of ITS write, which is
   * the one thing this component may not do to a consent control.
   *
   * @param {number} token - The emit token captured at submit
   * @param {Array<string>} changed - Unit ids whose state the submit moved (possibly empty)
   */
  _submitSucceeded(token, changed) {
    if (this._token !== token) return;
    this._busy = false;
    this.host.onLedgerChanged();
    this._renderSubmitPanel('success');
    this._submitTimer = setTimeout(() => {
      this._submitTimer = null;
      if (this._token !== token) return;
      // The changed ids ride the close call rather than being parked on the
      // registry: a one-shot set that never outlives this stack cannot be
      // spent by some later unrelated repaint.
      this.host.requestClose(changed);
      window.notificationManager?.showSuccess('Your sharing choices are saved.');
    }, SUBMIT_SUCCESS_MS);
  }

  /**
   * The submit was refused: back to the overview, batch intact, with the
   * server's own words (they name the offending unit) both inline and as a
   * toast.
   *
   * The toast is the ONE thing outside the gate: a refused consent write may
   * never be silent, whatever the user has since opened, and a notification
   * touches no panel state. Everything that does — `_busy`, the error line,
   * the repaint — is gated, so a stale rejection can neither un-disable the
   * successor's controls mid-write nor paint an overview over it.
   *
   * @param {number} token
   * @param {Object} err
   */
  _submitFailed(token, err) {
    const message = err?.error || SUBMIT_ERROR;
    window.notificationManager?.showError(message);
    if (this._token !== token) return;
    this._busy = false;
    this._submitError = message;
    this._repaint();
  }

  /**
   * Swap the overview body for the submit sequence's spinner/checkmark, on the
   * promoted `.wizard-confirmation*` chrome. Fresh DOM, no footer — there is
   * nothing to click while a consent batch is in flight.
   *
   * @param {'spinner'|'success'} phase
   */
  _renderSubmitPanel(phase) {
    if (!this._root) return;
    const inner = phase === 'spinner'
      ? `<div class="wizard-confirmation--spinner">
           <div class="wizard-confirmation__spinner"></div>
         </div>`
      : `<div class="wizard-confirmation--success">
           <div class="wizard-confirmation__success-icon">
             <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
               <polyline points="20 6 9 17 4 12"></polyline>
             </svg>
           </div>
           <h3 class="wizard-confirmation__success-title">Shared</h3>
         </div>`;
    this._root.innerHTML = this._queueBodyHtml(inner);
  }

  /* ------------------------------------------------------------------ *
   * Shared detail body (single + walk + drill)                          *
   * ------------------------------------------------------------------ */

  /**
   * The one detail layout (D2): heading (name; `h1` in single mode, `h3`
   * inside a run) → subtitle → the readiness note (`subLineFor`: revision /
   * nothing available / Full still preparing) → the compact depth radio rows
   * (every depth, unavailable ones disabled; staged one checked, `Suggested`
   * pill on `preselectDepth`'s pick) → the "What {partner} sees" emphasis plate showing the STAGED
   * depth's register text → downgrade notice → the "Where this is used"
   * expander. No commit control lives here; the footer owns it.
   *
   * @param {Object} unit - Live unit row
   * @returns {string} HTML
   */
  _unitBodyHtml(unit) {
    const staged = this._pending[unit.id] ?? seedStaged(unit);
    const partner = this.host.getPartnerName();
    const headingTag = this._mode === 'single' ? 'h1' : 'h3';

    return `
      <${headingTag} class="sharing-unit__name">${escapeHtml(unit.name)}</${headingTag}>
      <p class="sharing-unit__subtitle">Choose what ${escapeHtml(partner)} sees. You can change it anytime.</p>
      ${this._readinessNoteHtml(unit)}
      ${this._depthRowsHtml(unit, staged)}
      ${this._plateHtml(unit, staged, partner)}
      ${this._downgradeNoticeHtml(unit, staged)}
      ${this._usedSectionHtml(unit)}`;
  }

  /**
   * The compact depth selector — one radio row per depth, always all three so
   * every topic's control has the same shape (ink radio, title, muted
   * descriptor, and the blue `Suggested` pill on the depth `preselectDepth`
   * recommends, whether or not it is the staged one). A depth the unit does
   * not offer (its register not `verified`) renders disabled; the readiness
   * note above the rows says why. Clicking stages and swaps the plate; it
   * never advances or commits.
   *
   * @param {Object} unit - Live unit row
   * @param {'full'|'basic'|'off'} staged - The staged depth
   * @returns {string} HTML
   */
  _depthRowsHtml(unit, staged) {
    const suggested = preselectDepth(unit);
    const offered = offeredDepths(unit);
    const rows = ALL_DEPTHS.map(depth => {
      const checked = staged === depth;
      const unavailable = !offered.includes(depth);
      const tag = suggested === depth
        ? '<span class="notification-pill notification-pill--blue sharing-depth-row__tag">Suggested</span>'
        : '';
      return `
        <button type="button" class="sharing-depth-row${checked ? ' sharing-depth-row--selected' : ''}${unavailable ? ' sharing-depth-row--unavailable' : ''}"
                role="radio" aria-checked="${checked ? 'true' : 'false'}" aria-disabled="${unavailable ? 'true' : 'false'}"
                data-action="sharing-depth" data-state="${depth}"${unavailable || this._busy ? ' disabled' : ''}>
          <span class="sharing-depth-row__radio" aria-hidden="true"></span>
          <span class="sharing-depth-row__title">${DEPTH_LABELS[depth]}</span>
          <span class="sharing-depth-row__descriptor">${DEPTH_DESCRIPTORS[depth]}</span>
          ${tag}
        </button>`;
    }).join('');
    return `<div class="sharing-depth-rows" role="radiogroup" aria-label="Sharing depth">${rows}</div>`;
  }

  /**
   * The "What {partner} sees" preview — the style guide's emphasis plate
   * (`.sd-takeaway.sd-takeaway--plate` + `.sd-takeaway__label` eyebrow from
   * `components/takeaway.css`), with the staged level at the right of the
   * head row and that depth's verbatim register text below; Private shows
   * the empty line instead.
   *
   * @param {Object} unit - Live unit row
   * @param {'full'|'basic'|'off'} staged - The staged depth
   * @param {string} partner - Partner display name (unescaped)
   * @returns {string} HTML
   */
  _plateHtml(unit, staged, partner) {
    const empty = staged === 'off';
    const body = empty
      ? `<p class="sharing-unit__register sharing-unit__register--empty">Nothing from this topic reaches ${escapeHtml(partner)}.</p>`
      : `<p class="sharing-unit__register">${escapeHtml(unit.registers?.[staged]?.text || '')}</p>`;
    return `
      <div class="sd-takeaway sd-takeaway--plate sharing-unit__plate${empty ? ' sharing-unit__plate--empty' : ''}">
        <div class="sharing-unit__plate-head">
          <span class="sd-takeaway__label">What ${escapeHtml(partner)} sees</span>
          <span class="sharing-unit__plate-level">At ${DEPTH_LABELS[staged]}</span>
        </div>
        ${body}
      </div>`;
  }

  /**
   * The readiness/revision note. Reuses `subLineFor`'s locked strings so the
   * row and the panel can never disagree about why a unit is back in `Review`.
   *
   * @param {Object} unit
   * @returns {string} HTML, or ''
   */
  _readinessNoteHtml(unit) {
    const sub = subLineFor(unit);
    if (!sub) return '';
    return `<p class="sharing-unit__note sharing-unit__note--${sub.kind}">${escapeHtml(sub.text)}</p>`;
  }

  /**
   * The downgrade notice: the staged depth sits BELOW what some already-
   * frozen guide consumed. Upgrades are silent (R2).
   *
   * @param {Object} unit
   * @param {'full'|'basic'|'off'} depth - The staged depth
   * @returns {string} HTML, or ''
   */
  _downgradeNoticeHtml(unit, depth) {
    const behind = (unit.consumers || []).filter(c =>
      c.type === 'frozen' && DEPTH_RANK[depth] < DEPTH_RANK[c.consumedDepth]
    );
    if (!behind.length) return '';
    // The guide names are plain quoted text, not links: a link inline in
    // flowing paragraph text is a small target a thumb can land on mid-scroll,
    // and the router's mobile tap handler fires on touchstart, so an
    // incidental touch reads as a tap and yanks the user off this page. Each
    // link-out sits on its own row below, routing to that guide's /library
    // detail when the consumer carries its guideDefinitionId.
    const names = behind.map(c => `"${escapeHtml(c.name)}"`).join(', ');
    const links = behind
      .map(c => `<a class="sharing-unit__guide-link" href="${libraryHref(c.guideDefinitionId)}">Open “${escapeHtml(c.name)}” in Library →</a>`)
      .join('');
    return `
      <p class="sharing-unit__note sharing-unit__note--notice">Lowering this here won't change <strong>${names}</strong> — guides already generated keep showing what they used when they were created.</p>
      ${links}`;
  }

  /**
   * "Where this is used" — ONE expander for the whole section: a quiet toggle
   * row that reveals every consumer, fully expanded, in one click (name, date,
   * mutability body, the frozen rows' /library link-out). While collapsed, a
   * muted preview sits at the far right of the toggle row — "Ongoing
   * guidance", plus "+ N guide(s)" from `frozenConsumerCount` — and hides
   * once expanded (the list says it in full). The ongoing-guidance destination
   * stands as the first entry even before anything has crossed; frozen guide
   * rows follow when they exist.
   *
   * No revoke action and no guide-lifecycle machinery lives here: a frozen
   * guide's row links to /library ("open that guide"); the live gestalt
   * consumer is not a guide page, so it stays plain text.
   *
   * @param {Object} unit
   * @returns {string} HTML
   */
  _usedSectionHtml(unit) {
    const consumers = unit.consumers || [];
    // The ongoing-guidance destination is a STANDING first entry whether or
    // not anything has crossed yet. When the backend already lists live
    // consumers, those rows ARE the destination and the synthetic one yields.
    const rows = consumers.some(c => c.type === 'live')
      ? consumers
      : [{ type: 'live', name: 'Your ongoing guidance' }, ...consumers];
    const expanded = this._usedExpanded.has(unit.id);
    const guides = frozenConsumerCount(unit);
    const preview = expanded
      ? ''
      : `<span class="sharing-unit__used-preview">Ongoing guidance${guides ? ` + ${guides} guide${guides === 1 ? '' : 's'}` : ''}</span>`;
    let list = '';
    if (expanded) {
      const items = rows.map(consumer => {
        const frozen = consumer.type === 'frozen';
        const date = frozen ? formatShareDate(consumer.date) : '';
        // A SHORT, generic description per entry — no depth restated (the
        // plate above shows it) and no per-topic copy; the same words work for
        // every unit.
        const body = frozen
          ? 'A snapshot from when this guide was created — changes here don’t affect it.'
          : 'Feeds into advice conversations to improve personalization.';
        // The link-out is its own block below the body text, not inline in it:
        // a link sitting in flowing paragraph text is a small target a thumb
        // can land on mid-scroll, and the router's mobile tap handler fires on
        // touchstart, so that incidental touch reads as an intentional
        // navigation. A dedicated row with real padding keeps a scroll-start
        // touch from ever landing on the link.
        const link = frozen
          ? `<a class="sharing-unit__consumer-link" href="${libraryHref(consumer.guideDefinitionId)}">Open in Library →</a>`
          : '';
        return `
          <li class="sharing-unit__consumer sharing-unit__consumer--${frozen ? 'frozen' : 'live'}">
            <span class="sharing-unit__consumer-head">
              <span class="sharing-unit__guide">${frozen ? 'Guide - ' : ''}${escapeHtml(consumer.name)}</span>
              ${date ? `<span class="sharing-unit__consumer-date">${escapeHtml(date)}</span>` : ''}
            </span>
            <span class="sharing-unit__consumer-body">${body}</span>
            ${link}
          </li>`;
      }).join('');
      list = `<ul class="sharing-unit__consumer-list">${items}</ul>`;
    }
    return `
      <div class="sharing-unit__consumers">
        <div class="sharing-unit__used-head">
          <button type="button" class="sharing-unit__used-toggle"
                  data-action="sharing-used-toggle" aria-expanded="${expanded}">
            Where this is used
            <span class="sharing-unit__used-chevron" aria-hidden="true">${CHEVRON_SVG}</span>
          </button>
          ${preview}
        </div>
        ${list}
      </div>`;
  }

  /* ------------------------------------------------------------------ *
   * Interaction                                                         *
   * ------------------------------------------------------------------ */

  /**
   * Panel-internal delegation. Every control the panel renders is dispatched
   * here — the panel root lives in `RightPanelManager`'s content slot, outside
   * the host's own delegated container, so nothing collides. The INNERMOST
   * `[data-action]` wins, which is what lets the overview switch sit inside
   * its row's tap target without any propagation control.
   * @param {MouseEvent} event
   */
  _onRootClick(event) {
    const target = event.target.closest('[data-action]');
    if (!target || !this._root || !this._root.contains(target)) return;
    // Every control renders `disabled` while a write is in flight; this is the
    // belt to that braces, so a synthetic click can't interleave two writes.
    if (this._busy) return;
    const action = target.getAttribute('data-action');
    switch (action) {
      case 'sharing-depth':
        event.preventDefault();
        this._selectDepth(target.getAttribute('data-state'));
        break;
      case 'sharing-overview-stage': {
        event.preventDefault();
        const seg = target.closest('.partner-seg');
        if (seg) this._stage(seg.getAttribute('data-unit-id'), target.getAttribute('data-state'));
        break;
      }
      case 'sharing-overview-open':
        event.preventDefault();
        this._openFromOverview(target.getAttribute('data-unit-id'));
        break;
      case 'sharing-overview-intro':
        event.preventDefault();
        this._enterIntro();
        break;
      case 'sharing-intro-faq':
        event.preventDefault();
        this._toggleIntroFaq(target);
        break;
      case 'sharing-intro-done':
        event.preventDefault();
        this._enterOverview();
        break;
      case 'sharing-overview-walk':
        event.preventDefault();
        this._enterWalk();
        break;
      case 'sharing-overview-approve':
        event.preventDefault();
        this._approveRun();
        break;
      case 'sharing-overview-back':
        event.preventDefault();
        this._enterOverview();
        break;
      case 'sharing-overview-done':
      case 'sharing-single-done':
        event.preventDefault();
        this.host.requestClose();
        break;
      case 'sharing-single-approve':
        event.preventDefault();
        this._approveSingle();
        break;
      case 'sharing-used-toggle': {
        event.preventDefault();
        const id = this._unitId;
        if (this._usedExpanded.has(id)) this._usedExpanded.delete(id);
        else this._usedExpanded.add(id);
        this._repaint();
        break;
      }
      case 'sharing-queue-back':
        event.preventDefault();
        this._queueBack();
        break;
      case 'sharing-queue-next':
        event.preventDefault();
        this._queueNext();
        break;
      default:
        break;
    }
  }

  /**
   * A detail radio row was clicked — stage it for the displayed unit and
   * repaint (the plate swaps). Same in both modes: nothing commits here.
   * @param {string} value - `off` | `basic` | `full`
   */
  _selectDepth(value) {
    this._stage(this._unitId, value);
  }

  /**
   * Stage a depth for a unit. Only an offered (verified) depth is accepted — a
   * pending/failed one is never silently substituted — and a same-value click
   * is a no-op (no repaint).
   *
   * @param {string} unitId
   * @param {string} value - `off` | `basic` | `full`
   */
  _stage(unitId, value) {
    const unit = this._unit(unitId);
    if (!unit) return;
    if (!offeredDepths(unit).includes(value)) return;
    if (this._pending[unit.id] === value) return;
    this._pending[unit.id] = value;
    this._repaint();
  }

  /** Repaint whichever mode/phase is live, in place (never a re-emit). */
  _repaint() {
    if (this._mode === 'queue') this._paintQueue();
    else if (this._mode === 'single') this._paintSingle();
  }
}
